/**
 * 20260919 - IPHONE DUO SIDE DOCK: the live fold/safe-area state, and who gets to say what it is.
 *
 * ITS OWN STORE, not a uiStore slice — the same reasoning as inputCapabilitiesStore.ts next door,
 * and for the same reason it is load-bearing. uiStore statically imports embeddedBridge, which
 * composes the ENTIRE inbound handler table at module load, so any component that gains a uiStore
 * edge drags every bridge feature module into its own test module registry. Safe-area geometry has
 * to be readable from shell chrome all over the app, most of which has no other reason to know the
 * bridge exists. A leaf store — zustand plus foldState.ts, which imports nothing of ours — costs
 * them nothing.
 *
 * FOUR INPUTS, ONE ANSWER. They are kept apart and resolved on read rather than merged on write,
 * so a lower-precedence update can never clobber a higher one by arriving late:
 *   1. `bridge`  — Unity's `setFoldState` push (bridge/features/settings.ts). The only source that
 *                  can see the hinge, the fold rectangle or which display we are on; Safari exposes
 *                  none of it (foldState.ts documents the measurement).
 *
 *                  20260920 - THIS LINE USED TO READ "Not sent yet." and it was stale from the day
 *                  the senders landed (20260919), which made it the comment that would persuade the
 *                  next reader that a page reading `env` in every pose was BY DESIGN. Both hosts
 *                  send it now, on their page-ready path and from a per-frame poll:
 *                  TimelineWebViewPanel (board) and PurchasingWebViewPopupPanel (Projects home).
 *                  Arriving is a separate question from being sent — the Projects route has no
 *                  dispatch table and had to claim the verb by hand in its receiver chain
 *                  (components/Projects/projectsBridge.ts parseSetFoldState); both halves are
 *                  pinned by src/bridge/__tests__/nativeFoldStateDeliveryContract.test.ts.
 *   2. `harness` — the local preview harness, so a layout can be checked at every pose without a
 *                  Unity → Xcode → Simulator round trip. Ranks BELOW the bridge on purpose: with a
 *                  real host attached the real geometry wins, so a forgotten harness override can
 *                  never ship a wrong layout to a device.
 *   3. `url`     — 20260919. The insets Unity MEASURED NATIVELY and put in the page URL
 *                  (platform/urlSafeArea.ts), which is the only source that exists before the first
 *                  paint. A BOOT SNAPSHOT with an expiry, not a live source — see "Retiring the URL
 *                  seed" below. Ranks below `harness` so the harness can still preview a pose; in
 *                  practice the two never coexist, because the harness is never embedded.
 *   4. `env`     — what `env(safe-area-inset-*)` actually reports right now. Always available, and
 *                  the only source in standalone, online review and every browser.
 *
 * The `env` fallback is deliberately NOT a guessed pose: it carries the measured insets and the one
 * inference those support (deriveVerticalBarEdge), and leaves posture/division at "unknown"/null.
 * Neither is the `url` seed — but the seed is no longer four numbers. Since 20260919 it also carries
 * the native `verticalBarEdge` TRAIT, which is a statement rather than an inference and is the only
 * thing that can answer the Split View pose at all: there the pane's insets are honestly 0/0/0/0 and
 * the trait still names an edge (foldState.ts records the measurement). So the seed can say "there
 * is a rail on this edge" where `env` can only ever say "I see no asymmetry". Posture and the fold
 * rectangle still arrive over the bridge and nowhere else.
 *
 * AND THE TRAIT IS WHY THE BRIDGE IS NOT OPTIONAL IN THAT POSE. The URL seed EXPIRES — it is a boot
 * snapshot and cannot see a later pose change (see "Retiring the URL seed" below) — while `env` in
 * Split View reports zeros forever and can never re-derive the edge. The live answer is therefore
 * the `setFoldState` push and only that: a host that emits the URL fragment but never sends the verb
 * gets a correct first second and the header layout afterwards.
 *
 * 20260920 - AND ONE THING THE SEED SAYS OUTLIVES THE SEED. Not a fifth source: `seedOcclusion`
 * below is a LAST-KNOWN FACT, consulted only where the source that won has nothing to say.
 *
 * WHY THE EXPIRY IS RIGHT FOR FIVE FIELDS AND WRONG FOR THE SIXTH. The seed expires because `env()`
 * has its own opinion about the four insets and, through deriveVerticalBarEdge, about the edge: a
 * boot snapshot must not outrank the page's own live reading. `env()` has NO opinion whatsoever
 * about WHICH END of the strip the system's furniture sits at. A camera circle inside a strip that
 * is already outside the safe rect moves none of the four numbers — SideDock.module.css and the
 * cs:6132 work both say so at length — so there is no live reading for the seed to be outranked by.
 * Retiring the pair on the seed's clock does not hand the question back to a better answer; it
 * throws away the only answer that page will ever have and drops it onto resolveHousingEnds()'s
 * no-reading fallback.
 *
 * THAT IS THE OWNER'S 20260920 DEFECT, exactly. Same device, same pose, same session: the board
 * rendering the per-end reserve (~50px off the foot) while the Projects home beside it rendered the
 * both-ends fallback (~96px), leaving a visible gap under the hamburger. Both routes carry the URL
 * seed. So the seed now carries the pair (platform/urlSafeArea.ts) and the store holds it past the
 * seed's retirement — bounded by a pose change, which is the honest limit on how long a boot
 * snapshot may speak. See seedOcclusion and keepOcclusionSeed below.
 */

import { create } from 'zustand';
import {
  ASSUMED_FOLD_STATE,
  readOcclusionExtent,
  // 20260929 - the laptop band's shape check, shared with the setFoldState guard.
  readLaptopBand,
  resolveVerticalBarEdge,
  getEnvSafeAreaProbes,
  readEnvSafeArea,
  resolveSafeArea,
  // 20260929 - the display-corner stop-gap (see publishSafeAreaProperties).
  resolveUncoveredTopCornerSide,
  DISPLAY_CORNER_TOP_CLEARANCE_PX,
  type FoldState,
  type SafeAreaInsets,
} from '@/platform/foldState.ts';
import { readUrlSafeArea, type UrlSafeAreaSeed } from '@/platform/urlSafeArea.ts';
// 20260920 - A leaf module like foldState.ts: one flag and a timer, no store and no imports of
// ours, so it costs this graph nothing. See the mark in setEnvSafeArea.
import { markViewportChange } from '@/platform/viewportTransition.ts';

export interface FoldStateSources {
  /** Unity's last push, already stamped `source: 'native-bridge'`. */
  bridge: FoldState | null;
  /** The preview harness's override, already stamped `source: 'harness'`. */
  harness: FoldState | null;
  /** 20260919 - the native measurement carried by the page URL, or null when there wasn't one (every
   *  non-iOS host, every iOS host with nothing to report, and anything malformed). Null ALSO once it
   *  has been retired — this field is the seed's whole lifetime.
   *
   *  It is a SEED, not a rect: since the Split View measurement it also carries the native
   *  `verticalBarEdge` trait, which is the only thing that knows a rail belongs on an edge whose
   *  inset is zero (platform/urlSafeArea.ts). */
  url: UrlSafeAreaSeed | null;
  /** The browser's own answer, re-read on resize. */
  env: SafeAreaInsets;
  /**
   * 20260920 - NOT A FIFTH SOURCE, and the distinction is the whole design. The four above are
   * ranked and one of them WINS; this is a single fact held beside them, spent only where the
   * winner reports `null` for it. It can never displace a source, reorder the precedence, or answer
   * anything but the two occlusion ends. See OcclusionSeed.
   *
   * Null in every host that never had a seed carrying the pair — which is every non-iOS host, every
   * iOS host before 27.1, standalone, online review and the harness.
   */
  seedOcclusion: OcclusionSeed | null;
}

/**
 * 20260920 - THE SEED'S OCCLUSION PAIR, KEPT PAST THE SEED, AND THE INSETS THAT BOUND IT.
 *
 * WHAT IT IS. The one answer in the URL fragment that nothing on the web can re-derive and no later
 * measurement can supersede except a bridge push: which end of the reserved strip holds the
 * system's own furniture. The file header argues why this field alone outlives the retirement that
 * is correct for the other five.
 *
 * AND `safeArea` IS NOT BOOKKEEPING — IT IS THE EXPIRY. A boot snapshot may speak only for the
 * inset configuration it was measured in. On this device every pose change that moves the furniture
 * also moves the insets: a 180° turn swaps which side is reserved (left 84 ↔ right 84), a fold
 * changes the window, a rotation changes it again. So "the insets in force are no longer these
 * four" is a real and sufficient proxy for "the pose this was measured in is over", and
 * keepOcclusionSeed drops the pair on it.
 *
 * WHAT IT COSTS IF THAT PROXY IS EVER UNTRUE — i.e. if some future pose moves the housing from one
 * end of the strip to the other while reporting the same four insets: a stale `head: true,
 * foot: false` would leave the foot of the column unreserved with a camera sitting in it, which is
 * a control under the housing. That is precisely the cs:6132 regression, and it is the reason this
 * bound exists at all rather than the pair simply being held forever. The cheap failure is the
 * opposite direction — a dropped pair falls back to the both-ends geometry, which wastes space and
 * covers nothing.
 *
 * `head`/`foot` are real booleans, never null: urlSafeArea.ts takes the pair both-or-neither, so
 * "half a reading" is not a state that reaches here.
 */
export interface OcclusionSeed {
  head: boolean;
  foot: boolean;
  /** 20260926 - the extents the seed carried beside the pair (null when it carried none). Not a
   *  second lifetime: they are kept, spent and dropped exactly with `head`/`foot`. Optional so a
   *  seed written before the extents existed (and the tests that model one) still reads as "no
   *  extents"; absent and null mean the same thing. */
  headExtent?: number | null;
  footExtent?: number | null;
  /** The four insets the pair was measured alongside. Identity of this rect is the pair's lifetime. */
  safeArea: SafeAreaInsets;
}

/**
 * 20260919 - WHAT A PUSH IS ALLOWED TO LEAVE OUT, and the only field that is on that list.
 *
 * `source` is stamped here so a push can never claim to be something it is not. `splitView` is
 * OPTIONAL for a different reason: it is newer than the verb. A host built before the pane was
 * measured sends every other field and cannot send this one, and the honest reading of its silence
 * is `false` — "not reported as a pane" — which is also the safe direction (see FoldState.splitView:
 * a false negative costs a dock, a false positive lays a column over a full-screen page). It is
 * normalised to a real boolean on the way in, so nothing downstream ever sees `undefined` and the
 * FIELD itself stays required on FoldState, where the gate can rely on it.
 */
/**
 * 20260920 - AND THE TWO OCCLUSION FIELDS JOIN IT, for the same reason and with a different
 * default. They are newer than the verb too, so a host that predates them cannot send them — but
 * their absence must NOT normalise to `false`, because `false` is a measurement ("that end is
 * clear, spend nothing") and a host that cannot see the regions has measured nothing. They
 * normalise to `null`, which platform/foldState.ts resolveHousingEnds() turns back into the
 * geometry that shipped before the keys existed. `splitView` can flatten to a boolean precisely
 * because its false IS the honest reading of silence; these two cannot.
 */
/*
 * 20260926 - AND THE TWO EXTENTS JOIN THEM, newer again and defaulting the same way: absent is
 * `null` ("not reported"), which resolveHousingEnds() turns into the 96px allowance at an occluded
 * end.
 *
 * 20260929 - AND THE LAPTOP BAND, newer still: absent is `null` ("not the laptop pose"), which is
 * what every host that predates it means. Normalised through readLaptopBand on the way in.
 */
export type FoldStatePush =
  Omit<FoldState,
    'source' | 'splitView' | 'barHeadOccluded' | 'barFootOccluded' | 'barHeadExtent' | 'barFootExtent'
    | 'laptopBand'>
  & Partial<Pick<FoldState,
    'splitView' | 'barHeadOccluded' | 'barFootOccluded' | 'barHeadExtent' | 'barFootExtent' | 'laptopBand'>>;

/** A push is wire data: anything that is not a real boolean is "nobody said", not `false`. */
function normalizeOcclusion(value: boolean | null | undefined): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** 20260926 - the extents' twin of the above: a finite 0..400 number, else "nobody said". */
function normalizeExtent(value: number | null | undefined): number | null {
  return readOcclusionExtent(value);
}

interface FoldStateStore extends FoldStateSources {
  setBridgeFoldState: (s: FoldStatePush) => void;
  setHarnessFoldState: (s: FoldStatePush | null) => void;
  setEnvSafeArea: (i: SafeAreaInsets) => void;
  /** Drops the URL seed, permanently, so `env` governs from here on. Idempotent. Leaves
   *  `seedOcclusion` alone — see the implementation for why that is not an oversight. */
  retireUrlSafeArea: () => void;
}

function sameInsets(a: SafeAreaInsets, b: SafeAreaInsets): boolean {
  return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}

/**
 * "Has `env()` actually answered yet?" — the handover test for the URL seed.
 *
 * Any non-zero side will do. It cannot be "all four match the URL": a device with a notch but no
 * side strip reports the top first and the sides never change at all, and the two measurements come
 * from the same UIKit numbers anyway, so the first real reading is the handover. Note the converse
 * is NOT true and must not be assumed — an all-zero reading is a genuinely correct answer on an
 * iPad, a Mac, the Duo inner display and every browser (foldState.ts). That is exactly why this is
 * only ONE of the two retirement triggers; the other one is a clock.
 */
function hasAnyInset(i: SafeAreaInsets): boolean {
  return i.left > 0 || i.top > 0 || i.right > 0 || i.bottom > 0;
}

/**
 * 20260920 - WHOSE INSETS ARE ACTUALLY IN FORCE, in one place.
 *
 * The same precedence resolveFoldState applies and nothing else of it, so the setters can ask "did
 * the geometry this seed was measured in just stop being the geometry?" without a second reading of
 * the ranking. resolveFoldState calls it too (a bridge or harness state carries its own insets; the
 * fallback branch is `url ?? env`, which is what this returns when both are null), so there is one
 * implementation and it cannot drift from the answer the page is laid out by.
 */
function insetsInForce(s: Pick<FoldStateSources, 'bridge' | 'harness' | 'url' | 'env'>): SafeAreaInsets {
  return s.bridge?.safeArea ?? s.harness?.safeArea ?? s.url?.safeArea ?? s.env;
}

/**
 * 20260920 - the seeded pair's expiry, applied on every write that REPORTS insets.
 *
 * Keeps the pair while the insets in force are still the ones it was measured alongside, drops it
 * the moment they are not (OcclusionSeed carries the full argument and what a wrong bound costs).
 * Returns the SAME object when it keeps, so the resolve memo below is not invalidated by a write
 * that changed nothing.
 *
 * DELIBERATELY NOT CALLED FROM retireUrlSafeArea(). Retirement is a lifetime event, not a
 * measurement: the clock-expiry path hands the insets in force from the seed's four numbers to a
 * still-zero `env`, and validating against THAT would kill the pair for a pose change that never
 * happened — and then discard it just before the first real `env` reading arrives to confirm it. A
 * zero `env` resolves to no dock at all (deriveVerticalBarEdge answers null, splitView falls to
 * false), so nothing is spent in the gap; the next source to actually report insets re-checks the
 * pair, which is the only moment the check means anything.
 */
function keepOcclusionSeed(seed: OcclusionSeed | null, inForce: SafeAreaInsets): OcclusionSeed | null {
  if (seed === null) return null;
  return sameInsets(seed.safeArea, inForce) ? seed : null;
}

/** The pair a fresh URL seed contributes, or null when it carried none. Both-or-neither is already
 *  enforced by the parser; re-stated here as a type-level fact rather than re-checked. */
function occlusionSeedFrom(seed: UrlSafeAreaSeed | null): OcclusionSeed | null {
  if (seed === null) return null;
  const { barHeadOccluded: head, barFootOccluded: foot } = seed;
  if (typeof head !== 'boolean' || typeof foot !== 'boolean') return null;
  // 20260926 - the extents travel inside the same record, so they cannot outlive (or predecease)
  // the pair they describe.
  return {
    head, foot, headExtent: seed.barHeadExtent, footExtent: seed.barFootExtent, safeArea: seed.safeArea,
  };
}

// 20260919 - Both seeds are taken ONCE, here, at module evaluation.
//
// The URL seed is the entire point of this source: `location.search` is fully parsed before a line
// of our code runs, so it is the only geometry available to the FIRST render. (See urlSafeArea.ts
// for the contract and why an effect would be too late.)
//
// The guard below is the degenerate case: if `env` already has a real reading at this instant, the
// browser has beaten the seed and there is nothing for it to do — hand straight over rather than
// install a source that the very next re-read would retire. Not reachable in the embedded WebView
// (the IPC round trip has not happened yet, so this is zeros), but it is reachable from a restored
// page or a hand-typed URL, and the alternative would be a one-write flicker.
const urlSeed = readUrlSafeArea();
const envSeed = readEnvSafeArea();

// The degenerate-case guard: what it is asking is "has the browser already beaten the native
// measurement to a real reading?", and the seed's OTHER fields — the vertical-bar edge, the pane
// flag, the occlusion pair — are not things `env` can ever answer, so they play no part in it.
const seededUrl = urlSeed !== null && !hasAnyInset(envSeed) ? urlSeed : null;

// 20260920 - AND THE PAIR IS TAKEN EVEN WHEN THE SEED ITSELF IS NOT, because losing the seed to the
// guard above is not a reason to lose the one field the guard's winner cannot supply. It is put
// through the SAME expiry as every later write: if `env` really has beaten it, the insets in force
// are env's, and the pair survives only if they are the four it was measured beside.
const seededOcclusion = keepOcclusionSeed(
  occlusionSeedFrom(urlSeed),
  seededUrl !== null ? seededUrl.safeArea : envSeed,
);

export const useFoldStateStore = create<FoldStateStore>((set, get) => ({
  bridge: null,
  harness: null,
  url: seededUrl,
  seedOcclusion: seededOcclusion,
  // Seeded at creation so the first render is as right as it CAN be. A reserved side strip changes
  // where the shell's controls live, so the intent was the same "a wrong first frame is worse than
  // a late one" call initTileGrowth makes.
  //
  // 20260919 - BUT THE env SEED IS NOT SUFFICIENT, AND CANNOT BE MADE SO. In WKWebView the numbers
  // `env()` resolves against are not computed in the web process at all: the web process parses the
  // viewport arguments, tells the UI process, and the UI process computes the unobscured insets
  // from the view's own safeAreaInsets and pushes them BACK. That is an IPC round trip, so at
  // module-evaluation time — microseconds after index.html's pre-paint block turned the cover
  // viewport on, in the same task — the honest answer is still zeros, and no amount of forced
  // reflow can change it. Only wall-clock time can. Hence the settle ladder in
  // installEnvSafeAreaWatcher below: the seed gets the first frame, the ladder corrects it a frame
  // or two later. Do NOT "restore" the seed-only model — it is what produced the owner's
  // 20260919 report of Projects and Board both loading with the quiet controls in the top bar and
  // only jumping into the strip after a physical fold/unfold.
  //
  // AND THE LADDER IS NOT SUFFICIENT EITHER, WHICH IS WHY `url` EXISTS. The ladder's earliest rung
  // is the next animation frame, i.e. AFTER the first painted frame. One frame of wrong geometry is
  // invisible when it is a nudge; on the board it is not a nudge, because the top bar is unmounted
  // outright when a strip is reported (cs:6126), so the owner sees chrome appear and vanish on
  // every view load. Only a measurement that exists BEFORE first paint can fix that, and the only
  // one that does is the URL.
  env: envSeed,

  // `splitView: s.splitView === true` and not `?? false`: a push is wire data, so a value that is
  // neither `true` nor absent (a string, a 1) must land as `false` rather than as itself.
  //
  // 20260920 - EVERY WRITE THAT CARRIES INSETS ALSO RE-CHECKS THE SEEDED PAIR, in the same set().
  // A push and a harness pose both restate the geometry, so both are moments the boot snapshot's
  // pose may have ended (keepOcclusionSeed). Note the pair is NOT cleared just because this push
  // carries its own reading: it is superseded field-by-field at resolve time, where a pushed value
  // — `false` included — always wins, and where a push that says nothing falls back to it.
  setBridgeFoldState: (s) => set((state) => {
    const bridge: FoldState = {
      ...s,
      splitView: s.splitView === true,
      barHeadOccluded: normalizeOcclusion(s.barHeadOccluded),
      barFootOccluded: normalizeOcclusion(s.barFootOccluded),
      barHeadExtent: normalizeExtent(s.barHeadExtent),
      barFootExtent: normalizeExtent(s.barFootExtent),
      // 20260929 - absent or malformed is "not the laptop pose".
      laptopBand: readLaptopBand(s.laptopBand),
      source: 'native-bridge',
    };
    return {
      bridge,
      seedOcclusion: keepOcclusionSeed(
        state.seedOcclusion,
        insetsInForce({ bridge, harness: state.harness, url: state.url, env: state.env }),
      ),
    };
  }),
  setHarnessFoldState: (s) => set((state) => {
    const harness: FoldState | null = s === null ? null : {
      ...s,
      splitView: s.splitView === true,
      barHeadOccluded: normalizeOcclusion(s.barHeadOccluded),
      barFootOccluded: normalizeOcclusion(s.barFootOccluded),
      barHeadExtent: normalizeExtent(s.barHeadExtent),
      barFootExtent: normalizeExtent(s.barFootExtent),
      laptopBand: readLaptopBand(s.laptopBand),
      source: 'harness',
    };
    return {
      harness,
      seedOcclusion: keepOcclusionSeed(
        state.seedOcclusion,
        insetsInForce({ bridge: state.bridge, harness, url: state.url, env: state.env }),
      ),
    };
  }),

  // Field-by-field before set(), exactly as useAdaptiveGrid.ts compares its measurement: `resize`
  // fires in bursts (a rotation, a fold, a Split View drag) and almost every one of them reports
  // the same four numbers. A fresh object per event would invalidate the resolve memo below and
  // re-render every subscriber for nothing.
  //
  // 20260919 - IT IS ALSO THE HANDOVER POINT. The first re-read that reports a real inset retires
  // the URL seed, IN THE SAME WRITE. One write, not two: a separate retirement pass would publish
  // an intermediate state to every subscriber (and to the CSS custom properties), and while the
  // numbers would be identical the churn is free to avoid. On a real device the two measurements
  // are the same UIKit numbers, so this handover is invisible — which is the whole design.
  //
  // 20260920 - AND THE SEEDED OCCLUSION PAIR IS RE-CHECKED HERE TOO, AGAINST THE POST-HANDOVER
  // GEOMETRY. `url` may be retired by this very write, which moves the insets in force from the
  // seed's four numbers to these — so the check has to be made on the sources as they will BE, not
  // as they are. On a device the two readings are the same UIKit numbers, so the pair sails through
  // the handover and the Projects page keeps the end it was told about; on a rotation or a fold they
  // differ and the pair goes, which is exactly the bound OcclusionSeed argues for.
  //
  // The early return skips the check on purpose: an identical re-read reports no new geometry, and
  // the retirement it may still perform is a lifetime event rather than a measurement
  // (keepOcclusionSeed says why that distinction matters).
  setEnvSafeArea: (i) => {
    const state = get();
    const retireUrl = state.url !== null && hasAnyInset(i);
    if (sameInsets(state.env, i)) {
      if (retireUrl) set({ url: null });
      return;
    }
    const url = retireUrl ? null : state.url;
    const seedOcclusion = keepOcclusionSeed(
      state.seedOcclusion,
      insetsInForce({ bridge: state.bridge, harness: state.harness, url, env: i }),
    );
    // 20260920 - A CHANGED env READING IS THE DEVICE CHANGING SHAPE, so raise the snap signal
    // before the write. This is the re-read path — the settle ladder, the probe observer, the
    // resize/orientationchange handlers — and on this platform it is the ONLY notice the page gets
    // of an inset-only move (see installEnvSafeAreaWatcher: nothing fires for one). What follows is
    // `--safe-*` being republished under a layout that is already on screen, which re-pads every
    // shell and re-wraps the board; all of it has to snap.
    //
    // Only in the CHANGED branch — the early return above already swallowed an identical re-read,
    // and a ladder rung that reports the same four numbers is not a viewport change. The retirement
    // of the URL seed is deliberately not a mark either: it hands the same UIKit numbers from one
    // source to another (see the note above), so nothing moves.
    //
    // ...and only when the insets IN FORCE actually move (review, 20260920). With the bridge or the
    // harness answering - always, on the embedded board after the first push - an env reading changes
    // nothing that is published: `--safe-*` and the dock stay exactly where they were. Marking there
    // raised the snap flag over a layout that had not moved; the likely trigger is a PARK (the web view
    // pushed four screens down loses its top inset, and gets it back on unpark), i.e. just as the user
    // returns from a native picker - when the add-shot re-wrap, a user-initiated change, should glide.
    const insetsBefore = insetsInForce(state);
    const insetsAfter = insetsInForce({ bridge: state.bridge, harness: state.harness, url, env: i });
    if (!sameInsets(insetsBefore, insetsAfter)) markViewportChange();
    set(retireUrl ? { env: i, url: null, seedOcclusion } : { env: i, seedOcclusion });
  },

  // 20260920 - AND IT LEAVES `seedOcclusion` STANDING, which is the entire point of that field.
  // Retiring the seed hands the four insets and the edge back to `env`, which has a live opinion
  // about both. It has no opinion at all about which end of the strip the camera is in, so there is
  // nothing here to hand that question to — dropping the pair on this clock would replace an answer
  // with a fallback rather than with a better answer. See the file header and OcclusionSeed.
  retireUrlSafeArea: () => { if (get().url !== null) set({ url: null }); },
}));

// ── Resolution ──────────────────────────────────────────────────────────────────────────────
//
// Reference-stable by construction: the returned object is memoised on the IDENTITY of the three
// inputs. That is not an optimisation, it is a correctness requirement — zustand v5 resolves a
// selector through useSyncExternalStore, which throws "getSnapshot should be cached" and spins if
// the snapshot is a fresh object every call. Every input is replaced wholesale on change (and
// setEnvSafeArea refuses no-op writes), so identity is an exact test for "nothing changed".
let memo: (FoldStateSources & { out: FoldState }) | null = null;

/** Precedence: bridge > harness > url > env-derived. Pure apart from the memo. */
export function resolveFoldState(s: FoldStateSources): FoldState {
  if (memo !== null && memo.bridge === s.bridge && memo.harness === s.harness
      && memo.url === s.url && memo.env === s.env
      // 20260920 - `seedOcclusion` is part of the key because it is part of the answer. It changes
      // only when the pair is dropped, and a dropped pair moves the housing geometry.
      && memo.seedOcclusion === s.seedOcclusion) {
    return memo.out;
  }
  // `url` sits ABOVE `env` for exactly one reason: during boot `env` is a KNOWN-WRONG zero, not a
  // measurement (see the seed comments above). It sits BELOW `harness` so a preview pose still
  // wins — the harness is never embedded, so the pair never actually meets — and far below the
  // bridge, which is the same native measurement but live.
  //
  // Through insetsInForce so the setters' expiry check and this answer are the same reading of the
  // same ranking; in this branch bridge and harness are null, so it is exactly `url ?? env`.
  const measured: SafeAreaInsets = insetsInForce(s);
  const base: FoldState = s.bridge ?? s.harness ?? {
    ...ASSUMED_FOLD_STATE,
    safeArea: measured,
    // 20260920 - AND THE INSETS BEAT THE SEED'S EDGE WHENEVER THEY NAME A SIDE — the same
    // precedence the bridge push now uses, through the same function, because two transports
    // answering one pose differently is the class of defect this batch keeps finding.
    //
    // This branch reads the same snapshot on both sides (when the seed is answering, `measured` IS
    // `s.url.safeArea`), so a well-formed fragment cannot make the two disagree and nothing here
    // changes for one. What the shared rule buys is that it cannot START disagreeing: the seed's
    // trait is the system's PREFERRED edge, which it names "regardless of whether a vertical bar is
    // currently visible", and the dock is pinned flush to a window edge, so an unambiguous
    // asymmetric pair is the reading it has to be laid out from. See resolveVerticalBarEdge().
    //
    // The trait still carries the pose it exists for: four zeros, where the derivation is null by
    // design and the seed is the only thing that knows a rail belongs on that side. And a seed that
    // reports NO edge (Unspecified) still falls through to the insets, which costs nothing — a
    // device that reports Unspecified with an 84px asymmetric inset does not exist, and if it did,
    // the inset is the thing we could actually see.
    verticalBarEdge: resolveVerticalBarEdge(s.url?.verticalBarEdge, measured),
    // 20260919 - AND THE SEED'S PANE FLAG TRAVELS WITH ITS EDGE, because the two are one signal:
    // the rail is synthesized only where an edge, a zero inset and a PANE meet (foldState.ts,
    // resolveVerticalBarWidth). `env` can never supply this — a browser cannot see that its window
    // is one pane of an arrangement — so with no seed it is `false`, which is what every
    // non-embedded host means anyway.
    splitView: s.url?.splitView ?? false,
    // 20260920 - `env` CANNOT SEE AN OCCLUSION REGION AND NEVER WILL — a browser has no such API,
    // and the region is a circle inside a strip that is already outside the safe rect, so it moves
    // none of the four numbers. THE SEED CAN, since 20260920: the native fragment carries the pair
    // (urlSafeArea.ts), so while the seed is the answering source it answers this too. With no seed
    // it is `null` — "nobody could say" — which resolveHousingEnds() turns back into the both-ends
    // geometry. Written out rather than left to the ASSUMED_FOLD_STATE spread, because which source
    // can answer this is a decision and not an omission.
    barHeadOccluded: s.url?.barHeadOccluded ?? null,
    barFootOccluded: s.url?.barFootOccluded ?? null,
    // 20260926 - and the extents beside it, from the same source for the same reason.
    barHeadExtent: s.url?.barHeadExtent ?? null,
    barFootExtent: s.url?.barFootExtent ?? null,
    source: s.url !== null ? 'url' : 'env',
  };
  // 20260920 - THE LAST-KNOWN FACT, AND WHERE THE PRECEDENCE IS DELIBERATELY *NOT* TOUCHED.
  //
  // bridge > harness > url > env is unchanged: `base` above is whichever source won, whole. This
  // step only fills an occlusion end the winner reported `null` for — "nobody could say" — from the
  // pair the boot seed carried. It cannot change an inset, an edge, a posture or the source stamp,
  // and it cannot overrule a reading: a PUSHED value wins in both directions, `false` included,
  // because "measured clear" is an answer and a boot snapshot is not entitled to contradict it.
  //
  // WHY IT IS NEEDED AT ALL — and the field-by-field `??` rather than a pair swap. The seed retires
  // about a second after the first painted frame, and on a page that never receives a push (the
  // owner's 20260920 Projects report) that retirement is the moment the only occlusion answer the
  // page will ever have would otherwise vanish, dropping it onto the both-ends fallback while the
  // board next door renders the per-end reserve. Both transports send the pair both-or-neither, so
  // a half-filled state is not something our own senders can produce; per-field is simply the
  // narrowest rule that does the job.
  const seed = s.seedOcclusion;
  const out: FoldState = (seed === null
    || (base.barHeadOccluded !== null && base.barFootOccluded !== null))
    ? base
    : {
      ...base,
      barHeadOccluded: base.barHeadOccluded ?? seed.head,
      barFootOccluded: base.barFootOccluded ?? seed.foot,
      // 20260926 - EACH END'S EXTENT COMES FROM WHOEVER ANSWERED THAT END, never mixed: a pushed
      // `true` keeps the push's extent (null if it sent none), and an end the seed fills takes the
      // seed's. An extent measured beside one reading must not decorate another.
      barHeadExtent: base.barHeadOccluded !== null ? base.barHeadExtent : (seed.headExtent ?? null),
      barFootExtent: base.barFootOccluded !== null ? base.barFootExtent : (seed.footExtent ?? null),
    };
  memo = {
    bridge: s.bridge, harness: s.harness, url: s.url, env: s.env, seedOcclusion: s.seedOcclusion, out,
  };
  return out;
}

export function useFoldState(): FoldState {
  return useFoldStateStore(resolveFoldState);
}

// ── Publishing the resolved safe area to CSS ────────────────────────────────────────────────
//
// So a CSS module can inset by the reserved strip without the component subscribing to anything —
// the macTitlebarInset.ts pattern, and the reason the top bars there need no React edge either.
//
// THE `env()` DEFAULT STAYS THE DEFAULT. global.css defines all four `--safe-*` from `env()`, so in
// the embedded WebView — where the browser's numbers ARE the truth — these properties resolve
// without this file doing anything at all. An inline override on the root element is written ONLY
// when a source OTHER than `env` is answering, and is REMOVED the moment resolution falls back to
// `env`. That ordering matters both ways: it is what lets the harness preview a pose this browser
// is not actually in, and it is what guarantees a stale harness override cannot outlive the harness
// and quietly displace real geometry (--safe-bottom in particular is already consumed by the
// Transport and the Toast anchor, which predate all of this).
//
// 20260919 - AND THE `url` SEED MUST PUBLISH TOO. Not symmetry for its own sake: the CSS half of
// the first frame is the half that is wrong. `--safe-*` resolve from `env()`, and `env()` during
// boot is precisely the known-wrong zero the seed exists to replace, so a JS-only seed would fix
// the dock and leave every scrim's `--scrim-inset` (cs:6125) and every other `--safe-*` consumer
// laid out as if there were no reserved strip — half a first frame corrected is still a flash.
//
// THE HANDOVER DOES NOT REGRESS A FRAME. What triggers the common-path retirement is `env()`
// reporting a real number, so the value the CSS falls back to when these are removed is the value
// that was just removed: same UIKit numbers, one style recalc, and no intermediate state at all
// because the retirement is folded into the SAME store write (see setEnvSafeArea). The one case
// where the removal changes a number is the clock-expiry path with `env()` still at zero, and that
// is not a regression either — `env()` is the only truth CSS has, this file's inline copy was the
// same measurement taken earlier, and holding the override would leave the JS and CSS halves
// disagreeing for the life of the document.
const SAFE_AREA_PROPERTIES = ['--safe-left', '--safe-top', '--safe-right', '--safe-bottom'] as const;
/** 20260929 - the display-corner pair, published beside the four above (never folded into them). */
const CORNER_TOP_PROPERTIES = { left: '--safe-corner-top-left', right: '--safe-corner-top-right' } as const;

// 20260919 - AND WHAT IT PUBLISHES IS THE RESOLVED RECT, NOT THE RAW MEASUREMENT. When a pushed
// state names an edge whose inset is zero — Split View, measured — the page carves its own 84px
// rail and the dock fills it (foldState.ts, resolveVerticalBarWidth). The dock is
// `position: fixed` against the window edge, so it does not move anything by existing: the content
// only clears it because the shells pad by `--safe-*` and every centred dialog pads by
// `--scrim-inset` (cs:6133), all four of which are these properties. Publishing the measured zero
// while the dock claimed 84px would put a column of controls straight over the board.
//
// This is the same function useSideDock calls, for exactly that reason — one number, two consumers,
// no chance of them disagreeing. In the `env` path it is a no-op twice over: deriveVerticalBarEdge
// cannot name an edge under 44px, so there is never a zero inset to widen, and `splitView` is
// `false` there anyway — a browser cannot see that its window is one pane of an arrangement.
function publishSafeAreaProperties(state: FoldState): void {
  try {
    if (typeof document === 'undefined' || document.documentElement === null) return;
    const root = document.documentElement.style;
    if (state.source === 'env') {
      for (const property of SAFE_AREA_PROPERTIES) root.removeProperty(property);
      // 20260929 - and the corner pair with them: env() knows nothing about corners either.
      for (const property of Object.values(CORNER_TOP_PROPERTIES)) root.removeProperty(property);
      return;
    }
    const safeArea = resolveSafeArea(state);
    root.setProperty('--safe-left', `${safeArea.left}px`);
    root.setProperty('--safe-top', `${safeArea.top}px`);
    root.setProperty('--safe-right', `${safeArea.right}px`);
    root.setProperty('--safe-bottom', `${safeArea.bottom}px`);
    // 20260929 - THE DISPLAY-CORNER STOP-GAP (foldState.ts, resolveUncoveredTopCornerSide): 48px on
    // the uncovered top corner's side, nothing on the other. A SEPARATE pair on purpose — folding it
    // into the four above would un-dock the pose it fixes and pad every row of every tab. global.css
    // gates it to landscape as `--corner-top-left/right`, which is all a consumer reads.
    const corner = resolveUncoveredTopCornerSide(state);
    for (const side of ['left', 'right'] as const) {
      if (side === corner) root.setProperty(CORNER_TOP_PROPERTIES[side], `${DISPLAY_CORNER_TOP_CLEARANCE_PX}px`);
      else root.removeProperty(CORNER_TOP_PROPERTIES[side]);
    }
  } catch {
    // Publishing a custom property must never be able to break a store write.
  }
}

publishSafeAreaProperties(resolveFoldState(useFoldStateStore.getState()));
useFoldStateStore.subscribe((s) => publishSafeAreaProperties(resolveFoldState(s)));

// ── Keeping `env` current ───────────────────────────────────────────────────────────────────

/**
 * Keeps `env` current: re-reads `env(safe-area-inset-*)` on every signal that can mean the insets
 * moved, plus a bounded schedule for the ones that arrive with no signal at all. Returns a disposer
 * that undoes every listener, timer, frame, observer and window-global installed here.
 *
 * 20260919 - WHY THERE IS MORE HERE THAN TWO LISTENERS. The insets are NOT knowable when the store
 * seeds itself (see the seed comment above: WKWebView computes them in the UI process and pushes
 * them back over IPC, so the first sample of a freshly loaded document reads zeros), and on this
 * platform an inset-only change produces NO event: the WebView's frame is pinned to the full screen
 * by the native panel and content-inset adjustment is off, so the layout viewport never moves and
 * `resize` never fires. That combination is the owner-reported bug — Projects and Board each loaded
 * with the shell's quiet controls in the top bar, and only a physical fold/unfold (a genuine size
 * change, hence a genuine `resize`) moved them into the reserved strip. The CSS side self-heals for
 * free, because global.css resolves `--safe-*` straight from `env()`; only this JS-resolved branch
 * was frozen at the seed. Three additions below close it: a ResizeObserver on the probe, a finite
 * settle ladder, and a visibilitychange/host re-show re-read. All ADDITIVE. A fourth (20260919)
 * re-arms that ladder on `resize` as well, because entering Split View is a size change whose
 * inset/trait half lands after the event — see `onResize` for why that strengthens the resize
 * listener rather than weakening it.
 *
 * `resize` IS STILL THE LOAD-BEARING LISTENER, and nothing below may weaken it. A fold or unfold is
 * a resize with NO `orientationchange` and no orientation change of any kind, so anything keyed to
 * rotation misses it entirely — and this WebView drops `MediaQueryList`/`ResizeObserver` deliveries
 * across a rotation outright, which is why the observer added below is a SUPPLEMENT for the
 * inset-only case and can never stand in for `resize`. That drop is the same
 * symptom that put the same three-listener idiom into useRegularWidthLayout.ts and
 * useAdaptiveGrid.ts (an iPhone taken portrait → landscape → portrait came back on the WRONG
 * branch). `orientationchange` is re-fired on the NEXT animation frame because the event lands
 * BEFORE the new layout, so reading the probe synchronously in it returns the old insets.
 *
 * Re-reads that change nothing are free: setEnvSafeArea compares field-by-field first.
 */
export function installEnvSafeAreaWatcher(): () => void {
  if (typeof window === 'undefined') return () => {};

  const hasRaf = typeof requestAnimationFrame === 'function';
  const hasDocument = typeof document !== 'undefined';

  // ── The ResizeObserver rung: the ONLY signal that fires for an inset-only change ──────────
  //
  // ADDITIVE, never a replacement for `resize` (see the header above — this WebView drops RO and
  // MediaQueryList deliveries across a rotation, which is the whole reason `resize` is load-bearing).
  // What it adds is the case `resize` cannot see: the insets changing while the window does not.
  // That happens twice in normal use — the cover-viewport settle on every fresh load, and every
  // host hide/park/unpark/show, where the native side keeps the WebView alive at the same frame
  // (WebViewParking.cs; titlebarDragExclusions.ts:"nothing resizes on the way back").
  let observer: ResizeObserver | null = null;
  let observed: HTMLElement[] = [];

  /** Keep the observer pointed at the probes that actually exist right now — ALL of them. */
  const syncProbeObserver = () => {
    if (observer === null) return;
    const probes = getEnvSafeAreaProbes();
    if (probes.length === observed.length && probes.every((p, i) => p === observed[i])) return;
    for (const stale of observed) observer.unobserve(stale);
    observed = [...probes];
    // `box: 'border-box'` is not a detail, it is the whole of whether this works. A probe is
    // `width:0;height:0` and carries its insets as PADDING, so its CONTENT box is 0×0 whatever
    // env() reports — the default content-box observation would never fire once.
    //
    // 20260920 - AND THERE ARE TWO PROBES BECAUSE ONE BORDER BOX COULD NOT SEE A SWAP. It used to
    // be a single element carrying all four insets, so its box was (left+right) × (top+bottom) —
    // and the owner's 180° turn of the closed landscape pose swaps left and right, leaving that
    // sum identical to the pixel. No delivery, no resize (same size, same orientation class), so
    // the JS-resolved dock stayed on the old edge while the CSS half self-healed. platform/
    // foldState.ts PROBES carries the partition that fixes it; what this loop must never do is
    // observe only the first of them, which would put half the blindness straight back.
    for (const probe of observed) observer.observe(probe, { box: 'border-box' });
  };

  const reread = () => {
    useFoldStateStore.getState().setEnvSafeArea(readEnvSafeArea());
    // readEnvSafeArea may have just built fresh probes (first read of the document, or a wiped
    // body). Re-point the observer in the same turn so it can never be left on detached nodes.
    syncProbeObserver();
  };

  // ── The settle ladder: a FINITE, event-free re-read schedule ──────────────────────────────
  //
  // Belt to the observer's braces, and the rung that does not depend on the WebView emitting
  // anything — the failure mode this batch is fixing is precisely that it emits nothing.
  //
  // Why each property is what it is:
  //   • UNCONDITIONAL, fixed length, never self-re-arming. "Still zero" is NOT a retry signal:
  //     an iPad, a Mac window, every browser and the Duo INNER display all report a genuinely
  //     correct 0/0 (foldState.ts), so a stop-on-nonzero ladder would poll forever on every
  //     non-foldable host, and a "re-arm while it keeps changing" ladder could be kept alive
  //     indefinitely by a host whose insets legitimately alternate (a mid-rotation sample, or a
  //     parked view — WebViewParking.cs documents the bottom inset as unstable while parked).
  //   • IT MUST OUTLAST THE PER-VIEW VIEWPORT REWRITE. The pre-paint block is not the only
  //     viewport-arguments change: each view's mount effect rewrites the viewport meta again
  //     AFTER first commit, so there is a second, later settle to catch.
  //   • The frame rungs come first so the correction lands within a frame or two of first paint in
  //     the common case; the timeouts only cover the slow round trip.
  //   • Each rung costs one getComputedStyle on a 0×0 hidden node — no node creation, no mutation —
  //     and setEnvSafeArea discards an identical reading, so a rung that changes nothing costs
  //     literally nothing downstream. On every non-foldable host the WHOLE ladder is zero writes
  //     and zero re-renders.
  const SETTLE_DELAYS_MS = [100, 300, 600, 1000] as const;

  let settleFrames: number[] = [];
  let settleTimers: ReturnType<typeof setTimeout>[] = [];

  const clearSettle = () => {
    if (hasRaf) for (const frame of settleFrames) cancelAnimationFrame(frame);
    for (const timer of settleTimers) clearTimeout(timer);
    settleFrames = [];
    settleTimers = [];
  };

  // 20260919 - THE LAST RUNG ALSO RETIRES THE URL SEED, and the reason is worth spelling out
  // because the next person will read the expiry as a bug.
  //
  // The seed (platform/urlSafeArea.ts) is a BOOT SNAPSHOT: Unity measured the window's insets at
  // the moment it built the URL, and a URL cannot be re-sent. It therefore CANNOT see a later fold,
  // unfold or rotation. Left in place it would outrank `env` forever, and the first time the owner
  // folded the device after load the dock would stay pinned to the geometry the page happened to
  // boot with — the exact bug this whole batch is fixing, only permanent instead of transient.
  //
  // So it expires, on whichever of two triggers comes first:
  //   • `env` reports any real inset (setEnvSafeArea, above) — the common path, and invisible,
  //     because on a device the two sources are the same UIKit numbers; or
  //   • this rung, the end of the boot ladder — the backstop, for the host that never produces a
  //     non-zero `env` reading at all. Unconditional for the same reason the ladder itself is
  //     unconditional: "still zero" is a legitimate answer (iPad, Mac, browser, Duo inner display),
  //     so it can never be used as a reason to keep waiting.
  //
  // THE CLOCK STARTS AT THE FIRST FRAME, NOT AT LOAD (review, 20260919). The seed exists for one
  // frame: the first one PAINTED. Both Unity hosts load the page HIDDEN and reveal it only when
  // the web reports content-ready (TimelineWebViewPanel.TryReveal, an 8 s RevealTimeout behind
  // it), and a hidden WKWebView runs no animation frames at all — the same suspension the Assist
  // panel hit (cs:6034-39) — while DOM timers keep firing. A clock that started at load would
  // therefore run out BEHIND the hidden view on every board open that takes more than a second,
  // retire the seed before the page had painted once, and hand the reveal's first frame back to
  // whatever `env` says at that moment, which is the flash this file exists to remove. So the
  // last rung retires only once a frame has run; if it fires first, the whole ladder is re-armed
  // from the frame that eventually comes, and THAT ladder's last rung retires. The guarantee is
  // unchanged in substance — "the seed cannot outlive the first second" — it is just the first
  // second of VISIBILITY. A page that never paints keeps a seed nothing can see.
  //
  // Net effect: the URL governs the first frame, `env` governs every frame after the first second.
  // Re-arming (the host re-show hook) pushes the backstop out again, which is harmless — by then
  // the seed is almost always already retired, and if it is not, the page is still booting.
  let frameSeen = !hasRaf; // a host with no animation frames has nothing to wait for
  let expiredBeforeFirstFrame = false;

  const finishSettle = () => {
    reread();
    if (frameSeen) {
      useFoldStateStore.getState().retireUrlSafeArea();
      return;
    }
    // Only worth remembering while there is a seed to expire: with none, a re-armed ladder would
    // be nothing but four extra reads on a host that has already settled.
    expiredBeforeFirstFrame = useFoldStateStore.getState().url !== null;
  };

  /** Arms one ladder, replacing any ladder still in flight so re-arming cannot accumulate timers. */
  const startSettle = () => {
    clearSettle();
    if (hasRaf) {
      // Next frame, then the frame after it: the first is the earliest point the document has had a
      // real layout, the second brackets a push that arrived during it.
      settleFrames.push(requestAnimationFrame(() => {
        const restart = !frameSeen && expiredBeforeFirstFrame;
        frameSeen = true;
        expiredBeforeFirstFrame = false;
        reread();
        if (restart) { startSettle(); return; }
        settleFrames.push(requestAnimationFrame(reread));
      }));
    }
    const last = SETTLE_DELAYS_MS.length - 1;
    SETTLE_DELAYS_MS.forEach((delay, index) => {
      settleTimers.push(setTimeout(index === last ? finishSettle : reread, delay));
    });
  };

  // 20260919 - A RESIZE IS A SYNCHRONOUS RE-READ *AND* A FRESH LADDER.
  //
  // `resize` stays the load-bearing listener and is not weakened by a single character here: the
  // synchronous `reread()` still happens first, exactly as before. What is added is the ladder
  // behind it, and the reason is the Split View measurement (20260919). Entering, leaving or
  // re-balancing an arrangement is a genuine size change, so `resize` DOES fire for it — but the
  // half of the answer that matters in that pose is not the size: it is the safe-area/trait half,
  // which WKWebView computes in the UI process and pushes back over IPC (see the seed comment at
  // the top of this file). A synchronous read inside the resize handler therefore samples the NEW
  // window with the OLD insets, and on this platform nothing fires afterwards to correct it — an
  // inset-only change produces no event at all, which is the entire reason the ladder exists for
  // boot. A resize is the one moment we KNOW the insets are about to be restated, so it is the one
  // moment most worth re-arming for.
  //
  // IT CANNOT ACCUMULATE TIMERS, and this is worth stating because a resize arrives in bursts (a
  // rotation, a fold, a divider drag): startSettle() begins with clearSettle(), which cancels every
  // frame and timer of the ladder in flight and replaces both arrays. So at most ONE ladder exists
  // at any moment — a burst of fifty resize events leaves two rAFs and four timers pending, the
  // same as one. The nested `requestAnimationFrame(reread)` that a running ladder pushes lands in
  // whichever array is current when it is pushed, so it is tracked and cancelled too; it cannot
  // escape into an untracked frame. The only thing a re-arm moves is the URL seed's expiry, which
  // is pushed out by the ladder's length — harmless, and documented at finishSettle.
  const onResize = () => {
    reread();
    startSettle();
  };

  let raf = 0;
  const rereadNextFrame = () => {
    if (!hasRaf) { reread(); return; }
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(reread);
  };

  // A host that hides and re-shows the page. Cheap, and the insets can differ on the way back.
  const onVisibilityChange = () => {
    if (!hasDocument || document.visibilityState === 'visible') rereadNextFrame();
  };

  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(reread);
  }

  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', rereadNextFrame);
  if (hasDocument) document.addEventListener('visibilitychange', onVisibilityChange);

  // Same window-global convention as __previsReportTitlebarDragExclusions and the __flush*Sync
  // bridge hooks: the Unity hosts eval this when they re-show a WebView they had hidden themselves,
  // for which neither pageshow nor visibilitychange fires. Optional belt — the observer above
  // already catches the inset change on its own — but it costs one property and closes the case
  // where the host re-shows a view whose insets settle slowly.
  (window as unknown as Record<string, unknown>).__previsRereadSafeArea = startSettle;

  // Arm the boot ladder, and point the observer at the probe the store's seed already created.
  syncProbeObserver();
  startSettle();

  return () => {
    if (hasRaf) cancelAnimationFrame(raf);
    clearSettle();
    observer?.disconnect();
    observer = null;
    observed = [];
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', rereadNextFrame);
    if (hasDocument) document.removeEventListener('visibilitychange', onVisibilityChange);
    delete (window as unknown as Record<string, unknown>).__previsRereadSafeArea;
  };
}
